import {
  COMMUNITY_EVENTS_TOPIC,
  COMMUNITY_UPDATES_TOPIC,
  FEEDBACK_FOR_SCHOOLHOUSE_TOPIC,
  SAL_QA_TOPIC,
  STUDY_SPACES_TOPIC,
  TUTOR_LEARNING_CIRCLES_TOPIC,
  type VolunteerActivityType,
  type VolunteerTeamActivityType,
} from '@/queries/constants'
import {
  COMMUNITY_WORLD_SLUGS,
  ENRICHMENT_WORLD_SLUG,
  KEY_CLUB_WORLD_SLUG,
  SCHOOLHOUSE_TUTORS_WORLD_SLUG,
} from '@/client/services/worlds/constants'

// Midnight of this date, in the user's tz
export const VOLUNTEER_ACTIVITY_TRACKER_START_DATE = {
  month: 4,
  day: 20,
  year: 2022,
} as const

export const AUTOMATICALLY_LOGGED_VOLUNTEER_ACTIVITIES = [
  'host-session',
  'tutor-training',
  'onboarding-buddy-session',
  'attend-new-tutor-onboarding-session',
  'session-audited',
  'attend-sat-bootcamp-onboarding-session',
] as const

export const AUTOMATICALLY_LOGGED_VOLUNTEER_ACTIVITIES_SET = new Set<VolunteerActivityType>(
  AUTOMATICALLY_LOGGED_VOLUNTEER_ACTIVITIES
)

export const VOLUNTEER_TEAM_ACTIVITY_TYPE_TO_LABEL: Record<VolunteerTeamActivityType, string> = {
  'live-help': 'Live Help Team',
  auditor: 'Peer Reviewing',
  certification: 'Certification Team',
  'community-engagement': 'Community Engagement Team',
  blog: 'Blog Team',
  mentor: 'Coach Team',
  'policy-and-moderation': 'Content Review Team',
  india: 'India Team',
  safety: 'Safety Team',
  'social-media': 'Social Media Team',
  'tutoring-operations': 'Tutoring Operations Team',
  'user-support': 'User Support Team',
  'onboarding-buddy': 'Onboarding Buddy Team',
  'welcoming-team': 'Welcoming Team',
  'sat-onboarding': 'SAT Bootcamp Guides',
  moderator: 'Moderator Team',
}

export const VOLUNTEER_ACTIVITY_TO_LABEL: Record<VolunteerActivityType, string> = {
  ...VOLUNTEER_TEAM_ACTIVITY_TYPE_TO_LABEL,
  'tutor-training': 'Tutor Training',
  'host-session-prep': 'Session Prep',
  'host-session': 'Host Session',
  'coda-hours': 'Past Coda Hours',
  'onboarding-buddy-session': 'Onboarding Buddy (Attended Session)',
  'attend-new-tutor-onboarding-session': 'Attended New Tutor Onboarding Session',
  'session-audited': 'Audited Session',
  'attend-sat-bootcamp-onboarding-session': 'Attended SAT Bootcamp Onboarding Session',
  other: 'Other',
}

export const ACTIVITY_TYPE_TO_VOLUNTEER_TEAM_SLUG: Record<VolunteerTeamActivityType, string> = {
  mentor: 'coaching',
  certification: 'certification',
  'user-support': 'user-support',
  'policy-and-moderation': 'content-review',
  'social-media': 'social-media',
  blog: 'blog',
  'tutoring-operations': 'tutoring-operations',
  moderator: 'moderators',
  'live-help': 'live-help',
  india: 'india',
  safety: 'safety',
  'onboarding-buddy': 'onboarding-buddy',
  'welcoming-team': 'welcoming',
  'community-engagement': 'community-engagement',
  'sat-onboarding': 'sat-guides',
  auditor: 'peer-review',
}

export function isVolunteerTeamActivityType(
  type: VolunteerActivityType
): type is VolunteerTeamActivityType {
  return type in ACTIVITY_TYPE_TO_VOLUNTEER_TEAM_SLUG
}

export const VOLUNTEER_TEAM_SLUG_TO_ACTIVITY_TYPE: Record<string, VolunteerTeamActivityType> =
  Object.fromEntries(
    Object.entries(ACTIVITY_TYPE_TO_VOLUNTEER_TEAM_SLUG).map(([type, slug]) => [slug, type])
  ) as Record<string, VolunteerTeamActivityType>

export const LINK_REQUIRED_ACTIVITY_TYPES = new Set<VolunteerActivityType>([
  'host-session-prep',
  'coda-hours',
])

export const EXCLUDED_SESSION_TOPICS = [
  STUDY_SPACES_TOPIC,
  COMMUNITY_EVENTS_TOPIC,
  TUTOR_LEARNING_CIRCLES_TOPIC,
  SAL_QA_TOPIC,
  FEEDBACK_FOR_SCHOOLHOUSE_TOPIC,
  COMMUNITY_UPDATES_TOPIC,
]

const INCLUDED_WORLD_SLUGS = [KEY_CLUB_WORLD_SLUG, SCHOOLHOUSE_TUTORS_WORLD_SLUG]

export const EXCLUDED_WORLD_SLUGS = [...COMMUNITY_WORLD_SLUGS, ENRICHMENT_WORLD_SLUG].filter(
  slug => !INCLUDED_WORLD_SLUGS.includes(slug)
)

// Some volunteer hours are auto-approved on a cron-job. See VolunteerActivityQ.isAutoApprovableSessionPrep().
export const AUTO_APPROVED_SESSION_PREP_MAX_DURATION_SECONDS = 2700 // 45 minutes
export const AUTO_APPROVED_SESSION_PREP_DAYS_AGO_TO_APPROVE = 3
